import React from 'react';
import { MapPin, Users, Calendar, Briefcase, ExternalLink, Mail, Linkedin, Instagram, Award, Target, Zap, ArrowRight, Sparkles, Code2, Rocket, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useSiteConfig } from '@/hooks/use-site-settings';
import { useClients } from '@/hooks/use-clients';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import ProceduralGroundBackground from '@/components/ui/procedural-ground-background';
import { ThreeDMarquee } from '@/components/ui/3d-marquee';
import { getClientLogo, isValidImageUrl } from '@/data/mockImages';
import { Loader2 } from 'lucide-react';
import SEO, { personSchema, breadcrumbListSchema } from '@/components/SEO';

const cassioPortrait = 'https://i.imgur.com/5Jb2egt.png';

const seoDescription = "Conheça Cássio: Diretor de Arte e Designer Gráfico com 16 anos de experiência transformando ideias em experiências visuais impactantes. Especialista em criação de personagens digitais, desenvolvimento fullstack e modelagem 3D. Localizado no Rio de Janeiro.";

const seoBreadcrumbs = [
  { name: "Home", url: "https://cassio.art/" },
  { name: "Sobre", url: "https://cassio.art/sobre" }
];

const Sobre = () => {
  const { data: fetchedClients, isLoading: isLoadingClients } = useClients();
  const { data: siteConfig } = useSiteConfig();

  const skills = [
    'Adobe Creative Suite',
    'Figma',
    'Sketch',
    'Principle',
    'Cinema 4D',
    'After Effects',
    'React',
    'TypeScript',
    'Next.js',
    'Tailwind CSS',
    'Framer Motion',
    'Stable Diffusion',
    'Midjourney',
    'Blender',
    'KeyShot'
  ];

  const achievements = [
    {
      icon: Calendar,
      title: 'Anos de Experiência',
      value: '16+',
      description: 'Atuando no mercado brasileiro, transformando marcas desde 2014'
    },
    {
      icon: Users,
      title: 'Clientes Atendidos',
      value: '50+',
      description: 'De startups inovadoras a grandes corporações multinacionais'
    },
    {
      icon: Briefcase,
      title: 'Projetos Entregues',
      value: '120+',
      description: 'Soluções criativas e personalizadas que geram resultados reais'
    }
  ];

  const values = [
    {
      icon: Award,
      title: 'Excelência',
      description: 'Compromisso inabalável com qualidade em cada pixel, linha de código e interação'
    },
    {
      icon: Target,
      title: 'Foco no Resultado',
      description: 'Design que gera impacto mensurável nos negócios dos clientes'
    },
    {
      icon: Zap,
      title: 'Inovação Constante',
      description: 'Sempre explorando novas tecnologias, ferramentas e metodologias de trabalho'
    }
  ];

  const timeline = [
    {
      year: '2024',
      title: 'Especialização em IA Generativa',
      description: 'Foco estratégico em criação de personagens digitais consistentes, automação criativa com inteligência artificial e aplicação em workflows de design'
    },
    {
      year: '2022',
      title: 'Diretor de Arte Freelancer',
      description: 'Transição para carreira independente, ampliando portfólio com grandes marcas nacionais e internacionais, estabelecendo metodologia própria de trabalho'
    },
    {
      year: '2020',
      title: 'Lead Designer - Agência Digital',
      description: 'Liderança de equipe criativa de 8 profissionais, responsável por projetos de transformação digital para clientes do setor varejista e financeiro'
    },
    {
      year: '2018',
      title: 'Designer Sênior - Startup Tech',
      description: 'Desenvolvimento de produtos digitais B2B, interfaces de softwares SaaS e aplicativos mobile, colaborando diretamente com times de engenharia'
    },
    {
      year: '2014',
      title: 'Início da Carreira em Design',
      description: 'Primeira experiência profissional em agência de publicidade, trabalhando com criação de identidades visuais, materiais de comunicação e projetos de branding'
    }
  ];

  const clients = fetchedClients?.map(c => ({
    src: isValidImageUrl(c.logo) ? c.logo : getClientLogo(c.name),
    alt: c.name,
  })) || [];

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden relative">
      <SEO
        title="Sobre Cássio | Diretor de Arte & Designer Gráfico Especialista em IA - Rio de Janeiro"
        description={seoDescription}
        canonical="https://cassio.art/sobre"
        ogType="website"
        schema={{ ...personSchema, breadcrumb: breadcrumbListSchema(seoBreadcrumbs) }}
      />

      <ProceduralGroundBackground />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <section className="section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 mb-8 text-sm font-medium glassmorphism-card text-blue-400 rounded-full border border-blue-500/20 backdrop-blur-sm"
              >
                <MapPin className="w-4 h-4" />
                Rio de Janeiro, Brasil
              </motion.div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-8">
                Olá, eu sou o <span className="gradient-text-primary">Cássio</span>
              </h1>

              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed mb-6">
                Diretor de Arte & Designer Gráfico especialista em IA, com 16 anos
                transformando ideias em experiências visuais impactantes que conectam marcas
                ao futuro digital.
              </p>

              <p className="text-muted-foreground leading-relaxed mb-10 text-lg">
                Minha paixão está na intersecção entre criatividade tradicional e tecnologia
                emergente. Trabalho com criação de personagens digitais consistentes,
                desenvolvimento fullstack, modelagem 3D e motion graphics, sempre buscando soluções
                inovadoras que gerem resultados concretos e mensuráveis para meus clientes.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="liquid-glass-btn-solid rounded-full px-8 h-14 text-lg" asChild>
                  <Link to="/contato" className="flex items-center">
                    <Mail className="mr-2 h-5 w-5" />
                    Vamos Conversar
                  </Link>
                </Button>
                <Button variant="outline" size="lg" className="liquid-glass-outline rounded-full px-8 h-14 text-lg" asChild>
                  <Link to="/projetos" className="flex items-center">
                    <Sparkles className="mr-2 h-5 w-5" />
                    Ver Portfólio
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative z-10">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl premium-glow">
                  <img
                    src={cassioPortrait}
                    alt="Cássio - Diretor de Arte & Designer Gráfico"
                    className="w-full"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/20 via-transparent to-transparent" />
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                  className="absolute -top-6 -left-6 glassmorphism-card rounded-2xl p-5 border border-blue-500/30"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="h-6 w-6 text-blue-400" />
                    <p className="text-sm font-semibold text-white">16+ Anos</p>
                  </div>
                  <p className="text-xs text-gray-400">Experiência<br/>Profissional</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 }}
                  className="absolute -bottom-6 -right-6 glassmorphism-card rounded-2xl p-5 border border-indigo-500/30"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="h-6 w-6 text-indigo-400" />
                    <p className="text-sm font-semibold text-white">50+ Clientes</p>
                  </div>
                  <p className="text-xs text-gray-400">Satisfeitos<br/>E Recorrentes</p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="section-padding relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] opacity-10 pointer-events-none">
            <div className="w-full h-full bg-blue-500 blur-[150px] rounded-full" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-section mb-4">
              Resultados que <span className="gradient-text-primary">Falam por Si</span>
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Números que refletem o compromisso com qualidade, dedicação e os resultados concretos gerados para os clientes
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="glassmorphism-card rounded-2xl p-8 h-full card-hover-effect">
                  <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-2xl mx-auto mb-6">
                    <achievement.icon className="h-8 w-8 text-blue-400" />
                  </div>
                  <div className="text-4xl font-bold mb-2 gradient-text-primary">
                    {achievement.value}
                  </div>
                  <div className="heading-card-sm text-white mb-2">{achievement.title}</div>
                  <p className="text-muted-foreground text-sm">{achievement.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="section-padding relative overflow-hidden">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-section mb-4">
              O Que Me <span className="gradient-text-primary">Move</span>
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Valores fundamentais que guiam cada decisão, cada projeto e cada interação com os clientes
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="glassmorphism-card rounded-2xl p-8 h-full">
                  <value.icon className="h-12 w-12 text-blue-400 mx-auto mb-4" />
                  <h3 className="heading-card-sm mb-3 text-white">{value.title}</h3>
                  <p className="text-muted-foreground text-sm">{value.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-section mb-4">
              Ferramentas & <span className="gradient-text-primary">Tecnologias</span>
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Domínio completo das principais ferramentas de design gráfico e desenvolvimento web moderno
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap gap-3 justify-center">
              {skills.map((skill, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <Badge variant="secondary" className="px-4 py-2 text-sm font-medium glassmorphism-card hover:bg-white/10 transition-colors cursor-default border border-blue-500/20">
                    {skill}
                  </Badge>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-section mb-4">
              Trajetória <span className="gradient-text-primary">Profissional</span>
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Uma jornada de crescimento, aprendizado contínuo e evolução profissional em design, tecnologia e criação
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            {timeline.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative flex items-start pb-12 last:pb-0"
              >
                {index < timeline.length - 1 && (
                  <div className="absolute left-6 top-14 bottom-0 w-0.5 bg-gradient-to-b from-blue-500/50 to-transparent" />
                )}

                <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full font-bold text-white text-sm flex-shrink-0 z-10 shadow-lg">
                  {item.year.slice(2)}
                </div>

                <div className="ml-8 flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <Badge variant="outline" className="border-blue-500/30 text-blue-400 bg-blue-500/10">
                      {item.year}
                    </Badge>
                  </div>
                  <h3 className="heading-card mb-3 text-white">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="section-padding relative overflow-hidden">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-section mb-4">
              Marcas que <span className="gradient-text-primary">Confiaram</span>
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Privilegiado por colaborar com empresas incríveis e clientes que me deram a oportunidade de transformar suas marcas
            </p>
          </motion.div>

          {isLoadingClients ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : clients.length > 0 ? (
            <ThreeDMarquee images={clients} />
          ) : (
            <div className="text-center py-20">
              <p className="text-muted-foreground">Em breve, novos clientes serão adicionados ao portfólio</p>
            </div>
          )}
        </section>

        <section className="section-padding mb-20">
          <div className="glassmorphism-card rounded-3xl p-12 md:p-16 text-center relative overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-500/10 blur-[100px] rounded-full" />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative z-10"
            >
              <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-2xl mx-auto mb-6">
                <Rocket className="h-7 w-7 text-blue-400" />
              </div>

              <h2 className="heading-section mb-6 text-white">
                Vamos Criar Algo <span className="gradient-text-primary">Extraordinário</span> Juntos?
              </h2>

              <p className="text-body-lg text-muted-foreground mb-10 max-w-2xl mx-auto relative z-10">
                Estou sempre em busca de novos desafios e projetos que me permitam
                explorar a fronteira entre criatividade e tecnologia. Vamos conversar e
                transformar sua visão em realidade com um design impactante e estratégico.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="liquid-glass-btn-solid rounded-full px-8 h-14 text-lg" asChild>
                  <Link to="/contato">
                    Iniciar Projeto
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>

                <div className="flex gap-2">
                  {siteConfig?.social_links?.linkedin && (
                    <Button variant="outline" size="lg" className="liquid-glass-outline rounded-full w-14 h-14 p-0 flex items-center justify-center" asChild>
                      <a href={siteConfig.social_links.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                        <Linkedin className="h-5 w-5" />
                      </a>
                    </Button>
                  )}
                  {siteConfig?.social_links?.instagram && (
                    <Button variant="outline" size="lg" className="liquid-glass-outline rounded-full w-14 h-14 p-0 flex items-center justify-center" asChild>
                      <a href={siteConfig.social_links.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                        <Instagram className="h-5 w-5" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Sobre;
